import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { bookingsApi } from './api';
import { festiveService, type Festival } from '@/services/festive.service';
import type {
  Booking,
  BookingListParams,
  BookingListAllParams,
  BookingListResult,
  BookingStats,
  BookingMutationResult,
  BookingPaymentPayload,
  CancelBookingResult,
  CreateBookingPayload,
  CreateBookingResult,
  RemindersResult,
  SendReminderPayload,
  UpdateBookingPayload,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useBookings(
  params: BookingListParams | null,
): UseQueryResult<BookingListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'bookings',
      tenantId,
      params?.festival_id ?? '',
      params?.page ?? 1,
      params?.limit ?? 20,
      params?.status ?? '',
      params?.delivery_date ?? '',
      params?.location_id ?? '',
      params?.search ?? '',
    ],
    queryFn: () => bookingsApi.list(params as BookingListParams),
    enabled: Boolean(params?.festival_id),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Festival-wide KPI aggregate for the single-festival list view (GitHub issue
 * #289). The list query only returns one page, so the Upcoming ≤7d / Balance due
 * / Cancellation-rate KPIs must come from this server-side aggregate — computed
 * across all matching bookings — to stay constant as you paginate.
 */
export function useBookingStats(
  params: BookingListParams | null,
): UseQueryResult<BookingStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'bookings',
      tenantId,
      'stats',
      params?.festival_id ?? '',
      params?.status ?? '',
      params?.delivery_date ?? '',
      params?.location_id ?? '',
      params?.search ?? '',
    ],
    queryFn: () => bookingsApi.stats(params as BookingListParams),
    enabled: Boolean(params?.festival_id),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Every booking for one festival, fetched by walking all pages (GitHub issue
 * #292). The Booking Calendar builds each day-cell's count from the full set,
 * but the API silently caps ?limit at 100 (PaginationPipe), so a single wide
 * page undercounts any festival with >100 bookings and drops later dates off
 * the grid entirely. This walks page 1..N (N from meta.total) and concatenates.
 * Bounded by MAX_PAGES so a pathological festival can't fire unbounded requests;
 * when the cap is hit, `total` still reflects the true count so the caller can
 * show a "showing X of N" notice.
 */
const CALENDAR_PAGE_SIZE = 100;
const CALENDAR_MAX_PAGES = 50; // up to 5,000 bookings

export function useAllFestivalBookings(
  params: { festival_id: string; status?: BookingListParams['status'] } | null,
): UseQueryResult<BookingListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'bookings',
      tenantId,
      'calendar-all',
      params?.festival_id ?? '',
      params?.status ?? '',
    ],
    queryFn: async () => {
      const fetchPage = (page: number) =>
        bookingsApi.list({
          festival_id: params!.festival_id,
          page,
          limit: CALENDAR_PAGE_SIZE,
          status: params!.status || undefined,
        });
      const first = await fetchPage(1);
      const total = first.total;
      const totalPages = Math.min(
        Math.ceil(total / CALENDAR_PAGE_SIZE),
        CALENDAR_MAX_PAGES,
      );
      let data = first.data;
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2)),
        );
        data = data.concat(...rest.map((r) => r.data));
      }
      return { data, total };
    },
    enabled: Boolean(params?.festival_id),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Cross-festival bookings — powers the "All festivals" option on the list page
 * (GitHub issue #296). A SINGLE server-side paginated, filtered query against
 * GET /bookings — so the request count no longer grows with the festival count,
 * and pagination reflects the true server-side total. Replaces the previous
 * per-festival fan-out + client-side merge/filter/paginate.
 */
export function useAllBookings(
  params: BookingListAllParams | null,
): UseQueryResult<BookingListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'bookings',
      tenantId,
      'all-festivals',
      params?.festival_id ?? '',
      params?.page ?? 1,
      params?.limit ?? 20,
      params?.status ?? '',
      params?.delivery_date ?? '',
      params?.location_id ?? '',
      params?.search ?? '',
    ],
    queryFn: () => bookingsApi.listAll(params as BookingListAllParams),
    enabled: params !== null,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Cross-festival KPI aggregate for the "All festivals" list view (GitHub issue
 * #296, mirroring #289). Served by GET /bookings/stats across every matching
 * booking — never a single page — so the Upcoming ≤7d / Balance due /
 * Cancellation-rate KPIs reflect true server-side totals rather than a
 * client-side slice.
 */
export function useAllBookingsStats(
  params: BookingListAllParams | null,
): UseQueryResult<BookingStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'bookings',
      tenantId,
      'all-festivals',
      'stats',
      params?.festival_id ?? '',
      params?.status ?? '',
      params?.delivery_date ?? '',
      params?.location_id ?? '',
      params?.search ?? '',
    ],
    queryFn: () => bookingsApi.statsAll(params as BookingListAllParams),
    enabled: params !== null,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useBooking(bookingId: string | undefined): UseQueryResult<Booking> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['bookings', tenantId, 'detail', bookingId],
    queryFn: () => bookingsApi.getById(bookingId as string),
    enabled: Boolean(bookingId),
    staleTime: 15_000,
  });
}

export function useBookingReminders(bookingId: string | undefined): UseQueryResult<RemindersResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['bookings', tenantId, 'reminders', bookingId],
    queryFn: () =>
      bookingId ? bookingsApi.reminders(bookingId) : Promise.resolve({ reminders: [] } as RemindersResult),
    enabled: Boolean(bookingId),
    staleTime: 10_000,
  });
}

/**
 * Festival lookup — powers the required festival selector on List & Calendar.
 * Pulls a generous page so the selector can show recent + upcoming festivals
 * without a follow-up fetch.
 */
export function useFestivalsLookup(): UseQueryResult<Festival[]> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['bookings', tenantId, 'festivals-lookup'],
    queryFn: async () => {
      const res = await festiveService.listFestivals({ page: 1 });
      return res.data;
    },
    staleTime: 60_000,
  });
}

function invalidateBookings(qc: ReturnType<typeof useQueryClient>, tenantId: string) {
  qc.invalidateQueries({ queryKey: ['bookings', tenantId] });
}

export function useCreateBooking() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation<CreateBookingResult, Error, { festivalId: string; payload: CreateBookingPayload }>({
    mutationFn: ({ festivalId, payload }) => bookingsApi.create(festivalId, payload),
    onSuccess: () => invalidateBookings(qc, tenantId),
  });
}

export function useUpdateBooking() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation<Booking, Error, { id: string; payload: UpdateBookingPayload }>({
    mutationFn: ({ id, payload }) => bookingsApi.update(id, payload),
    onSuccess: () => invalidateBookings(qc, tenantId),
  });
}

export function useCancelBooking() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation<CancelBookingResult, Error, { id: string }>({
    mutationFn: ({ id }) => bookingsApi.cancel(id),
    onSuccess: () => invalidateBookings(qc, tenantId),
  });
}

export function useAddDeposit() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation<BookingMutationResult, Error, { id: string; payload: BookingPaymentPayload }>({
    mutationFn: ({ id, payload }) => bookingsApi.addDeposit(id, payload),
    onSuccess: () => invalidateBookings(qc, tenantId),
  });
}

export function usePayBalance() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation<BookingMutationResult, Error, { id: string; payload: BookingPaymentPayload }>({
    mutationFn: ({ id, payload }) => bookingsApi.payBalance(id, payload),
    onSuccess: () => invalidateBookings(qc, tenantId),
  });
}

export function useSendReminder() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation<
    { booking_id: string; reminder_sent: boolean; channel: string; sent_at: string },
    Error,
    { id: string; payload: SendReminderPayload }
  >({
    mutationFn: ({ id, payload }) => bookingsApi.sendReminder(id, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['bookings', tenantId, 'reminders', vars.id] });
    },
  });
}
